import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { FormField, SelectField } from '@components/web/FormField'
import Button from '@components/web/Button'
import PasswordStrength from '@components/web/PasswordStrength'
import StepIndicator from '@components/web/StepIndicator'
import { useProviderAuth } from '@hooks/useProviderAuth'
import { usePageMeta } from '@hooks/usePageMeta'

const STEPS = [{ label: 'Personal Info' }, { label: 'Professional Info' }]

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
  { value: 'Prefer not to say', label: 'Prefer not to say' },
]

const SPECIALTY_OPTIONS = [
  { value: 'General Practice', label: 'General Practice' },
  { value: 'Nursing', label: 'Nursing' },
  { value: 'Physiotherapy', label: 'Physiotherapy' },
  { value: 'Elderly Care', label: 'Elderly Care' },
  { value: 'Pediatrics', label: 'Pediatrics' },
  { value: 'Mental Health', label: 'Mental Health' },
  { value: 'Nutrition & Dietetics', label: 'Nutrition & Dietetics' },
  { value: 'Post-Surgical Care', label: 'Post-Surgical Care' },
  { value: 'Other', label: 'Other' },
]

interface Step1 {
  name: string
  email: string
  phone: string
  gender: string
  dateOfBirth: string
  password: string
  confirmPassword: string
}

interface Step2 {
  specialty: string
  licenseNumber: string
  experience: string
}

interface Errors {
  name?: string; email?: string; phone?: string; gender?: string
  dateOfBirth?: string; password?: string; confirmPassword?: string
  specialty?: string; licenseNumber?: string; experience?: string
  general?: string
}

export default function ProviderSignUp() {
  const navigate = useNavigate()
  const { signup, validateEmail, validatePassword, validatePhoneNumber } = useProviderAuth()
  usePageMeta({
    title: 'Provider Sign Up | HomiCareplus',
    description: 'Create a HomiCareplus provider account to accept care requests, manage availability, and grow your healthcare practice online.',
  })
  const [step, setStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Errors>({})

  const [s1, setS1] = useState<Step1>({ name: '', email: '', phone: '', gender: '', dateOfBirth: '', password: '', confirmPassword: '' })
  const [s2, setS2] = useState<Step2>({ specialty: '', licenseNumber: '', experience: '' })

  const setS1Field = (field: keyof Step1) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setS1((prev) => ({ ...prev, [field]: e.target.value }))

  const validateStep1 = (): boolean => {
    const e: Errors = {}
    if (!s1.name.trim() || s1.name.trim().length < 2) e.name = 'Full name required (min 2 characters)'
    if (!validateEmail(s1.email)) e.email = 'Enter a valid email address'
    if (!validatePhoneNumber(s1.phone)) e.phone = 'Enter a valid phone number'
    if (!s1.gender) e.gender = 'Please select your gender'
    if (!s1.dateOfBirth) e.dateOfBirth = 'Date of birth is required'
    const { isValid, errors: pwdErrors } = validatePassword(s1.password)
    if (!isValid) e.password = pwdErrors[0]
    if (s1.password !== s1.confirmPassword) e.confirmPassword = 'Passwords do not match'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const validateStep2 = (): boolean => {
    const e: Errors = {}
    if (!s2.specialty) e.specialty = 'Please select your specialty'
    if (!s2.licenseNumber.trim()) e.licenseNumber = 'License number is required'
    const exp = Number(s2.experience)
    if (!s2.experience || isNaN(exp) || exp < 0 || exp > 60) e.experience = 'Enter valid years of experience (0–60)'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleNext = () => {
    if (validateStep1()) setStep(1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateStep2()) return

    setIsLoading(true)
    const result = await signup({
      name: s1.name.trim(),
      email: s1.email.trim().toLowerCase(),
      phone: s1.phone.trim(),
      gender: s1.gender,
      dateOfBirth: s1.dateOfBirth,
      password: s1.password,
      specialty: s2.specialty,
      licenseNumber: s2.licenseNumber.trim(),
      experience: s2.experience,
    })
    setIsLoading(false)

    if (!result.success) {
      setErrors({ general: result.error || 'Signup failed. Please try again.' })
      return
    }

    navigate(`/auth/provider-email-verification?email=${encodeURIComponent(s1.email)}`)
  }

  return (
    <div className="bg-card rounded shadow-lg p-5 sm:p-6 lg:p-7 w-full">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="mb-4 pb-4 text-center">
        <h2 className="text-2xl font-bold text-foreground">Join as Healthcare Provider</h2>
        <p className="text-muted-foreground text-sm mt-1">Create your healthcare provider account</p>
      </div>

      <div className="mb-5">
        <StepIndicator steps={STEPS} currentStep={step} />
      </div>

      {errors.general && (
        <div className="mb-4 p-3 rounded bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{errors.general}</p>
        </div>
      )}

      {step === 0 ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Full Name" type="text" placeholder="Full Name" value={s1.name} onChange={setS1Field('name')} error={errors.name} required autoComplete="name" className="h-11 rounded" />
            <FormField label="Email Address" type="email" placeholder="Email Address" value={s1.email} onChange={setS1Field('email')} error={errors.email} required autoComplete="email" className="h-11 rounded" />
            <FormField label="Phone Number" type="tel" placeholder="Phone Number" value={s1.phone} onChange={setS1Field('phone')} error={errors.phone} required autoComplete="tel" className="h-11 rounded" />
            <SelectField label="Gender" options={GENDER_OPTIONS} value={s1.gender} onChange={(v) => setS1((p) => ({ ...p, gender: v }))} error={errors.gender} placeholder="Select gender" required />
            <FormField label="Date of Birth" type="date" value={s1.dateOfBirth} onChange={setS1Field('dateOfBirth')} error={errors.dateOfBirth} required className="h-11 rounded" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
            <div className="space-y-2">
              <FormField label="Password" type="password" placeholder="Min 12 characters" value={s1.password} onChange={setS1Field('password')} error={errors.password} required autoComplete="new-password" className="h-11 rounded" />
              <PasswordStrength password={s1.password} />
            </div>
            <FormField label="Confirm Password" type="password" placeholder="Re-enter your password" value={s1.confirmPassword} onChange={setS1Field('confirmPassword')} error={errors.confirmPassword} required autoComplete="new-password" className="h-11 rounded" />
          </div>

          <Button className="w-full mt-1 h-11 rounded" size="lg" onClick={handleNext} rightIcon={<ArrowRight size={18} />}>
            Next: Professional Info
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField label="Specialty" options={SPECIALTY_OPTIONS} value={s2.specialty} onChange={(v) => setS2((p) => ({ ...p, specialty: v }))} error={errors.specialty} placeholder="Select specialty" required />
            <FormField label="Medical License Number" type="text" placeholder="e.g. MDN-12345" value={s2.licenseNumber} onChange={(e) => setS2((p) => ({ ...p, licenseNumber: e.target.value }))} error={errors.licenseNumber} required className="h-11 rounded" />
            <FormField label="Years of Experience" type="number" placeholder="e.g. 5" min="0" max="60" value={s2.experience} onChange={(e) => setS2((p) => ({ ...p, experience: e.target.value }))} error={errors.experience} required className="h-11 rounded sm:col-span-2" />
          </div>

          <div className="flex gap-3">
            <Button variant="outline" type="button" className="flex-1 h-11 rounded" onClick={() => setStep(0)} leftIcon={<ArrowLeft size={16} />}>
              Back
            </Button>
            <Button type="submit" className="flex-1 h-11 rounded" size="lg" isLoading={isLoading}>
              Create Account
            </Button>
          </div>
        </form>
      )}

      <p className="text-center text-sm text-muted-foreground mt-5">
        Already have an account?{' '}
        <Link to="/auth/login?role=provider" className="text-primary font-semibold hover:underline">Login</Link>
      </p>
    </div>
  )
}
